import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { DASH, areaUomAbbrev, formatNumber } from '@miq/utils';
import { formatPercent } from '@angular/common';

@Component({
  selector: 'sp-building-summary, [spBuildingSummary]',
  template: `<div class="flex-container flex-container--h-alignment mt-sm mb-sm">
    <div class="item">
      <div class="item--label">{{ 'pv.totalRentableSpace' | xTranslate }}</div>
      <div class="item--value net-rentable-area">{{ netRentableArea() }}</div>
    </div>
    <div class="vertical-divider"></div>
    <div class="item">
      <div class="item--label">{{ 'pv.totalAvailableSpace' | xTranslate }}</div>
      <div class="item--value available-space">
        {{ computedAvailableSpace() }} ({{ computedAvailableSpaceRatio() }})
      </div>
    </div>
  </div>`,
  styleUrls: ['./building-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BuildingSummaryComponent {
  netRentableArea = signal<string>(DASH);
  computedAvailableSpace = signal<string>(DASH);
  computedAvailableSpaceRatio = signal<string>(DASH);

  #setNetRentableArea(building: Record<string, any>) {
    const { building_info } = building ?? {};
    if (building_info?.tgt_net_rentable_area == null) {
      return this.netRentableArea.set(DASH);
    }

    const netRentableArea = formatNumber(building_info?.tgt_net_rentable_area, 'en-US', '1.0-0');
    const netRentableAreaUOM = areaUomAbbrev('Square Feet');
    this.netRentableArea.set(`${netRentableArea} ${netRentableAreaUOM}`);
  }

  #setComputedAvailableValues(building: Record<string, any>) {
    const { building_info } = building ?? {};
    if (building_info?.tgt_usage_total_available_area == null) {
      return this.computedAvailableSpace.set(DASH);
    }

    const availableSpace = formatNumber(building_info.tgt_usage_total_available_area, 'en-US', '1.0-0');
    const availableSpaceUOM = areaUomAbbrev('Square Feet');
    this.computedAvailableSpace.set(`${availableSpace} ${availableSpaceUOM}`);
  }

  #setComputedAvailableSpaceRatio = (building: Record<string, any>) => {
    const { building_info } = building ?? {};
    const availableArea = building_info?.tgt_usage_total_available_area ?? 0;
    const netRentableArea = building_info?.tgt_net_rentable_area;

    if (netRentableArea === 0 || netRentableArea == null) {
      return this.computedAvailableSpaceRatio.set(DASH);
    }
    this.computedAvailableSpaceRatio.set(formatPercent(availableArea / netRentableArea, 'en-US', '1.0-1'));
  };

  @Input() set building(building: Record<string, any>) {
    this.#setNetRentableArea(building);
    this.#setComputedAvailableValues(building);
    this.#setComputedAvailableSpaceRatio(building);
  }
}
